import { useEffect, useRef, useState, useCallback } from 'react';
import { api, authToken } from '../lib/api';
import { loadTripQueue, saveTripQueue, migrateTripQueue, clearTripQueue } from '../lib/tripQueue';
import { useWakeLock } from '../hooks/useWakeLock';
import { useAuth } from '../lib/auth';
import { Satellite, MapPinOff, X, Download, ShieldCheck, AlertTriangle, Smartphone } from 'lucide-react';
import { isNativeApp, nativeNotify, initNativeShell } from '../lib/native';
import { subscribeLocation } from '../lib/locationService';
import { hasNativeTracker } from '../lib/nativeTracker';

/*
 * TripTracker — continuous, best-effort GPS breadcrumb collector for
 * in-progress travel reimbursements.
 *
 * Web platform limits (informational, not bugs):
 *   • `watchPosition` only fires while the tab is visible on iOS Safari.
 *     Locking the screen suspends the tab.
 *   • On Android Chrome (installed as PWA), Wake Lock keeps the screen on
 *     which keeps the tab foregrounded and GPS ticking.
 *   • Anything more (true background) needs a native shell.
 *
 * What this component does about it:
 *   a. Screen Wake Lock       — keeps display awake while a trip runs.
 *   b. beforeunload guard     — warns before closing tab mid-trip.
 *   c. sendBeacon on pagehide — final flush that survives page unload.
 *   d. Auto-restart on error  — re-arms watchPosition every 15 s if it errors.
 *   e. Rich coord metadata    — sends speed / heading / altitude when available.
 *   f. Adaptive sampling      — 10 s flush for first 5 min, then 30 s.
 *   g. Contextual install nudge — for non-installed clients during a trip.
 */

const INSTALL_HINT_KEY  = 'sahakar_tracker_install_hint_dismissed';
const REFRESH_TRIPS_MS  = 60_000;
const FLUSH_FAST_MS     = 10_000;
const FLUSH_SLOW_MS     = 30_000;
const ADAPTIVE_CUTOFF_MS = 5 * 60_000;
const HEARTBEAT_MS      = 5 * 60_000;   // guaranteed position ping, even while parked
const MIN_MOVE_METERS   = 5;

const haversineM = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371008.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '')}/api`
  : '/api';

const NATIVE = isNativeApp();
// The Android background service adds the trip route itself, even with the app closed.
const NATIVE_SERVICE = hasNativeTracker();


export default function TripTracker() {
  const { user } = useAuth();
  const [activeTrips, setActiveTrips] = useState([]);
  const [collected, setCollected] = useState(0);
  const [geoError, setGeoError] = useState(null);
  const { active: wakeLockOn, release: releaseWakeLock } = useWakeLock(!NATIVE && !!user && activeTrips.length > 0);
  const [hidden, setHidden] = useState(false);
  const [lastPingAt, setLastPingAt] = useState(null);
  const [showInstallHint, setShowInstallHint] = useState(() => {
    try { return localStorage.getItem(INSTALL_HINT_KEY) !== '1'; } catch { return true; }
  });

  const watchUnsubRef   = useRef(null);
  const prevTripCountRef = useRef(0);
  const lastPointRef    = useRef({});   // per-trip { lat, lng }
  const queueRef        = useRef({});
  const queueOwnerRef   = useRef(null);
  const flushBusy       = useRef(false);
  const userId = user?.id;
  const trackingStartRef = useRef(null);
  const flushTimerRef   = useRef(null);
  const retryTimerRef   = useRef(null);
  const saveQueue = useCallback((queue) => saveTripQueue(userId, queue), [userId]);
  useEffect(() => {
    if (queueOwnerRef.current && queueOwnerRef.current !== userId) clearTripQueue(queueOwnerRef.current);
    queueOwnerRef.current = userId;
    queueRef.current = loadTripQueue(userId);
    setCollected(Object.values(queueRef.current).reduce((n, rows) => n + rows.length, 0));
    setActiveTrips([]);
  }, [userId]);

  // ─────── Poll active trips ───────
  const refreshTrips = useCallback(async () => {
    if (!user) { setActiveTrips([]); return; }
    try {
      const { data } = await api.get('/reimbursements', { params: { mine: true, status: 'in_progress' } });
      if (queueOwnerRef.current !== user.id) return;
      const trips = (data.rows || []).filter(r => r.type === 'travel');
      migrateTripQueue(user.id, new Set(trips.map(t => t.id)), queueRef.current);
      setActiveTrips(trips);
    } catch (err) { console.debug('active trips refresh failed (transient)', err); }
  }, [user]);

  useEffect(() => {
    refreshTrips();
    const iv = setInterval(refreshTrips, REFRESH_TRIPS_MS);
    return () => clearInterval(iv);
  }, [refreshTrips]);

  useEffect(() => { initNativeShell(); }, []);

  // Native shell: system notifications on trip start / end
  useEffect(() => {
    if (!NATIVE) return;
    const prev = prevTripCountRef.current;
    const now = activeTrips.length;
    if (prev === 0 && now > 0) nativeNotify(9001, 'Trip tracking started', 'Background GPS recording is on — you can lock your screen or switch apps.');
    if (prev > 0 && now === 0) nativeNotify(9003, 'Trip tracking stopped', 'Your trip has ended. GPS recording is off.');
    prevTripCountRef.current = now;
  }, [activeTrips.length]);

  // ─────── watchPosition + (d) auto-restart ───────
  const appendPoint = useCallback((point) => {
    for (const t of activeTrips) {
      const prev = lastPointRef.current[t.id];
      // Heartbeats always land — they are the 5-minute proof of position, even when parked.
      if (!point.heartbeat && prev && haversineM(prev, point) < MIN_MOVE_METERS) continue;
      lastPointRef.current[t.id] = { lat: point.lat, lng: point.lng };
      queueRef.current[t.id] = (queueRef.current[t.id] || []).concat(point);
    }
    if (queueOwnerRef.current !== userId) return;
    saveQueue(queueRef.current);
    setCollected(Object.values(queueRef.current).reduce((n, arr) => n + arr.length, 0));
  }, [activeTrips, saveQueue, userId]);

  const startWatch = useCallback(() => {
    trackingStartRef.current ||= Date.now();
    if (watchUnsubRef.current) return;
    watchUnsubRef.current = subscribeLocation({
      distanceFilter: 5, // trips want the fine-grained route
      onPoint: (p) => { setGeoError(null); appendPoint({ ...p, timestamp: p.timestamp || new Date().toISOString() }); },
      onError: (err) => {
        setGeoError(err?.message || 'GPS unavailable');
        if (NATIVE) nativeNotify(9002, 'GPS signal lost', 'Sahakar is retrying — check location permission if this persists.');
      },
    });
  }, [appendPoint]);

  const stopWatch = useCallback(() => {
    if (watchUnsubRef.current) { watchUnsubRef.current(); watchUnsubRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    trackingStartRef.current = null;
  }, []);

  useEffect(() => {
    if (!user || activeTrips.length === 0 || NATIVE_SERVICE) { stopWatch(); return; }
    startWatch();
    return stopWatch;
  }, [user, activeTrips, startWatch, stopWatch]);

  // ─────── Adaptive flush (f) ───────
  const flushNow = useCallback(async () => {
    if (flushBusy.current || !userId) return;
    flushBusy.current = true;
    try {
    const activeIds = new Set(activeTrips.map(t => t.id));
    for (const tripId of Object.keys(queueRef.current)) {
      if (!activeIds.has(tripId)) { delete queueRef.current[tripId]; continue; }
      const batch = queueRef.current[tripId] || [];
      if (batch.length === 0) continue;
      try {
        await api.post(`/reimbursements/${tripId}/trip/breadcrumb`, { points: batch });
        if (queueOwnerRef.current !== userId) return;
        // GPS may append while this upload is in flight; remove only the sent prefix.
        queueRef.current[tripId] = (queueRef.current[tripId] || []).slice(batch.length);
      } catch { /* retry next tick */ }
    }
    if (queueOwnerRef.current !== userId) return;
    saveQueue(queueRef.current);
    setCollected(Object.values(queueRef.current).reduce((n, arr) => n + arr.length, 0));
    } finally { flushBusy.current = false; }
  }, [activeTrips, saveQueue, userId]);

  useEffect(() => {
    if (!user || activeTrips.length === 0) return;
    let cancelled = false;
    const schedule = () => {
      if (cancelled) return;
      const elapsed = Date.now() - (trackingStartRef.current || Date.now());
      const interval = elapsed < ADAPTIVE_CUTOFF_MS ? FLUSH_FAST_MS : FLUSH_SLOW_MS;
      flushTimerRef.current = setTimeout(async () => {
        await flushNow();
        schedule();
      }, interval);
    };
    schedule();
    return () => { cancelled = true; if (flushTimerRef.current) clearTimeout(flushTimerRef.current); };
  }, [user, activeTrips, flushNow]);

  // ─────── Guaranteed 5-minute heartbeat ping ───────
  useEffect(() => {
    if (!user || activeTrips.length === 0 || NATIVE_SERVICE) return;
    const ping = () => {
      if (!('geolocation' in navigator)) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = pos.coords;
          appendPoint({
            lat: c.latitude,
            lng: c.longitude,
            accuracy: Number.isFinite(c.accuracy) ? c.accuracy : undefined,
            speed: Number.isFinite(c.speed) && c.speed >= 0 ? c.speed : undefined,
            heading: Number.isFinite(c.heading) ? c.heading : undefined,
            altitude: Number.isFinite(c.altitude) ? c.altitude : undefined,
            timestamp: new Date(pos.timestamp || Date.now()).toISOString(),
            heartbeat: true,
          });
          setLastPingAt(new Date());
          flushNow();
        },
        (err) => setGeoError(err.message || 'GPS unavailable'),
        { enableHighAccuracy: true, maximumAge: 60_000, timeout: 30_000 }
      );
    };
    ping();                                   // one immediately, then every 5 minutes
    const iv = setInterval(ping, HEARTBEAT_MS);
    return () => clearInterval(iv);
  }, [user, activeTrips, appendPoint, flushNow]);

  // ─────── (c) sendBeacon final flush on tab close ───────
  useEffect(() => {
    if (!user || activeTrips.length === 0) return;
    const beaconFlush = () => {
      try {
        const token = authToken();
        for (const tripId of Object.keys(queueRef.current)) {
          const batch = queueRef.current[tripId] || [];
          if (!batch.length) continue;
          const url  = `${API_BASE}/reimbursements/${tripId}/trip/breadcrumb`;
          // Auth is required on this endpoint. Beacon can't set headers; we
          // fall back to `fetch(..., { keepalive: true })` which DOES support
          // custom headers and is honoured by browsers during unload.
          const body = JSON.stringify({ points: batch });
          try {
            fetch(url, {
              method: 'POST',
              headers: token
                ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
                : { 'Content-Type': 'application/json' },
              body,
              keepalive: true,
              credentials: 'include',
            });
          } catch { /* last-resort: sendBeacon (no auth, may 401) */
            navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }));
          }
        }
      } catch { /* silent */ }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') beaconFlush(); };
    const onPageHide = () => beaconFlush();

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [user, activeTrips]);

  // ─────── (b) beforeunload guard (web only) ───────
  useEffect(() => {
    if (NATIVE || activeTrips.length === 0) return;
    const onBeforeUnload = (e) => {
      const total = Object.values(queueRef.current).reduce((n, arr) => n + arr.length, 0);
      const msg = total > 0
        ? `You have ${total} unsent GPS point${total === 1 ? '' : 's'}. Close anyway?`
        : `You have ${activeTrips.length} trip${activeTrips.length > 1 ? 's' : ''} being tracked. Close anyway?`;
      e.preventDefault();
      e.returnValue = msg;
      return msg;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [activeTrips]);

  // ─────── UI ───────
  if (!user || activeTrips.length === 0 || hidden) return null;

  const standalone = isStandalone();
  const nudgeInstall = !NATIVE && !standalone && showInstallHint;

  const dismissInstallHint = () => {
    setShowInstallHint(false);
    try { localStorage.setItem(INSTALL_HINT_KEY, '1'); } catch { /* noop */ }
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-40 rounded-xl bg-sky-950/95 backdrop-blur border border-sky-500/40 text-sky-50 shadow-2xl px-3 py-2.5 flex flex-col gap-2 max-w-xs"
      data-testid="trip-tracker-chip"
    >
      <div className="flex items-center gap-2">
        {geoError ? (
          <MapPinOff size={16} className="text-amber-400 shrink-0"/>
        ) : (
          <Satellite size={16} className="text-emerald-400 shrink-0 animate-pulse"/>
        )}
        <div className="text-xs leading-tight min-w-0 flex-1">
          <div className="font-semibold">
            {geoError ? 'Tracking paused' : `Tracking · ${activeTrips.length} trip${activeTrips.length > 1 ? 's' : ''}`}
          </div>
          <div className="text-sky-300/80 text-[10px] truncate">
            {geoError
              ? `${geoError}${NATIVE ? '' : ' — retrying every 15s'}`
              : NATIVE_SERVICE ? 'Route recorded even when the app is closed'
              : `${collected} point${collected === 1 ? '' : 's'} queued · ${NATIVE ? 'background tracking on' : 'keep this tab open'}`}
          </div>
          {!NATIVE_SERVICE && <div className="text-sky-300/70 text-[10px] truncate" data-testid="tracker-last-ping">
            5-min ping: {lastPingAt ? lastPingAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'waiting…'}
          </div>}
        </div>
        <button
          onClick={() => setHidden(true)}
          className="p-1 -mr-1 text-sky-300/70 hover:text-sky-50"
          title="Hide indicator (tracking continues)"
          data-testid="trip-tracker-hide"
        ><X size={13}/></button>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-1.5 text-[10px]">
        {NATIVE && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="tracker-native-badge">
            <Smartphone size={9}/> Native background tracking
          </span>
        )}
        {!NATIVE && wakeLockOn && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="tracker-wakelock-on">
            <ShieldCheck size={9}/> Screen locked awake
          </span>
        )}
        {!NATIVE && !('wakeLock' in navigator) && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20" title="Screen may sleep, pausing GPS">
            <AlertTriangle size={9}/> No wake-lock support
          </span>
        )}
      </div>

      {/* (g) Contextual install nudge */}
      {nudgeInstall && (
        <div
          className="flex items-start gap-2 pt-2 border-t border-sky-500/25"
          data-testid="tracker-install-nudge"
        >
          <Download size={12} className="text-sky-300 mt-0.5 shrink-0"/>
          <div className="text-[10px] leading-snug flex-1">
            <span className="text-sky-100">Install this app to your home screen for the most reliable tracking.</span>
            <button
              onClick={dismissInstallHint}
              className="ml-1 underline text-sky-300 hover:text-sky-100"
              data-testid="tracker-install-nudge-dismiss"
            >Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
